import {
  Band,
  brand,
  Cta,
  DownloadStrip,
  Eyebrow,
  Page,
  PanelBar,
  Point,
  Rule,
  Runner,
  ui,
} from '../ui';
import type { LauncherBuild } from '../site-config';
import { useSiteConfig } from '../site-config';
import { isLauncher } from '../is-launcher';
import { column } from './shared';
import { useDocumentTitle } from '../title';

/**
 * The launcher, and how to get it.
 *
 * **The store sold games and then said nothing about how to run one.** Build
 * distribution has been wired end to end for a while — `gbcli` uploads chunks,
 * the API serves a manifest, the launcher resolves what to install and fetches
 * the bytes — and there was no page anywhere in the product that offered the
 * launcher to the person who had just bought something. A player's path ended
 * at a receipt.
 *
 * ## One button, above everything else
 *
 * **The download used to be the fourth thing on the page**, under a heading,
 * a lead paragraph and a panel bar, in a row of three platform buttons two of
 * which have never worked. Steam's install page is the argument for the other
 * shape: a dark field the width of the window, a headline, and one button, with
 * the file's details in small type beneath it. Everything explaining *why* the
 * launcher exists sits below the fold, because somebody who came here to
 * install it has already decided.
 *
 * So the hero is `bleed` — edge to edge, between the bar and the padded content
 * — rather than a section in the column. `Page` documents why: a hero
 * constrained to a text column is the clearest tell that a games site was built
 * as a business site.
 *
 * It is Ink by hand rather than `Band`, and the difference is only where it
 * sits: `Band` floods with negative margins from inside the content column, and
 * `bleed` is already outside it. `data-gb-surface="ink"` is what makes the type
 * roles correct on black — see `tokens.css` — so nothing inside the hero needs
 * to know what it is standing on. The primary button stays Solar, because the
 * Ink surface deliberately does not touch `--gb-accent`.
 *
 * ## Windows, said once, in the right places
 *
 * `launcherBuilds` is one entry now. macOS and Linux were removed there rather
 * than hidden here, and this page must not put them back by implication: the
 * button names its platform, the requirements list carries the fact that the
 * other two do not exist yet *and why*, and a visitor who is plainly not on
 * Windows is told before they download an installer they cannot run.
 *
 * That last check is deliberately three-valued. `looksLikeWindows` answers
 * null for anything it does not recognise and the note is drawn only on a
 * definite no, so a user-agent this does not know costs nothing — the button is
 * there for everybody either way, because a sniff is a hint and a download is a
 * decision.
 *
 * **The buttons are honest about not existing yet.** Signed installers are
 * published wherever a release goes and their URL comes from the API's
 * environment; until one is set, `Cta` draws the button as a real button marked
 * unavailable rather than as a plausible link that 404s. That is section 29.2,
 * and it matters more here than on any other page on the site: the file this
 * page fails to hand over is the one that installs games.
 *
 * Not to be confused with `/downloads`, plural, which is a screen inside the
 * launcher listing what it is installing. This is the page that offers the
 * launcher itself — and if you are reading it *in* the launcher, it says so and
 * points at that screen instead of handing you an installer you are running.
 */
export function DownloadRoute(): React.ReactElement {
  useDocumentTitle('Get the launcher');

  const site = useSiteConfig();

  /*
   * The first entry, not a platform lookup.
   *
   * There is one build and the array is what carries it — see `launcherBuilds`
   * for why the shape survived the other two platforms being removed. A page
   * that searched for `label === 'Windows'` would be a second place the
   * platform is named, and the one that goes stale first.
   */
  const build: LauncherBuild | null = site.data?.launcher.builds[0] ?? null;
  const version = site.data?.launcher.version ?? null;
  const inLauncher = isLauncher();

  /*
   * What the page calls the platform, in one place.
   *
   * The button has to be able to name it before `GET /v1/site` lands — the
   * shape of the page is the same whether or not there is a release, which is
   * the whole argument for the pending button — so this cannot simply be
   * `build.label`. It falls back to the only platform there is, and reads the
   * build's own label once it has one, so a second platform arriving changes
   * the button, the runner and the heading together.
   */
  const platform = build?.label ?? 'Windows';

  return (
    <Page
      site="store"
      bleed={
        <Hero
          build={build}
          platform={platform}
          version={version}
          inLauncher={inLauncher}
          pending={site.isPending}
        />
      }
    >
      <Runner left="The launcher" right={platform} />

      <PanelBar />

      <section className={ui.stack ?? ''}>
        <h2 className="gb-subhead">What you are getting</h2>

        {/*
          The file's own facts, as a list rather than a paragraph.

          `.details` is the mono-label grid the reviewed screens use, and this is
          the same kind of content: short answers to questions somebody asks in
          a fixed order before downloading anything. A row is absent rather than
          empty when its fact is not known — a `VERSION —` line says nothing
          except that we are missing one.
        */}
        <dl className={ui.details ?? ''}>
          <dt className={ui.detailLabel ?? ''}>What it is</dt>
          <dd className={ui.detailValue ?? ''}>A signed Windows installer.</dd>

          {/*
            The build's own note, and absent rather than guessed at.

            A hardcoded fallback here would be a second statement of what the
            installer runs on — `launcherBuilds` owns that sentence — and the
            copy that goes stale is always the one nobody remembers is a copy.
          */}
          {build ? (
            <>
              <dt className={ui.detailLabel ?? ''}>Works on</dt>
              <dd className={ui.detailValue ?? ''}>{build.note}</dd>
            </>
          ) : null}

          {version ? (
            <>
              <dt className={ui.detailLabel ?? ''}>Version</dt>
              <dd className={ui.detailValue ?? ''}>{version}</dd>
            </>
          ) : null}

          <dt className={ui.detailLabel ?? ''}>Cost</dt>
          <dd className={ui.detailValue ?? ''}>Nothing. It is free.</dd>

          {/*
            Where the two removed platforms are accounted for, and the only
            place they are named. Two buttons that had never worked said *this
            product is half-finished*; one sentence says *this product is for
            Windows*, which is the true thing.
          */}
          <dt className={ui.detailLabel ?? ''}>Mac and Linux</dt>
          <dd className={ui.detailValue ?? ''}>
            Not yet. A Mac build has to be signed by Apple before it will open, and we cannot do
            that yet.
          </dd>
        </dl>
      </section>

      <Band tone="ink">
        <Eyebrow>What happens next</Eyebrow>
        <h2 className="gb-headline">Three steps</h2>

        {/*
          The strip counting the steps, which is the device doing its own job.

          Section 03 covers any bounded process, and three steps is one — so
          `DownloadStrip` here is *step n of 3*, not decoration, and its
          `aria-label` says exactly that. Equal bars, because nothing in a step
          number is a score.
        */}
        <div className={brand.g3 ?? ''}>
          {STEPS.map((step, index) => (
            <div key={step.title} style={column(3)}>
              <DownloadStrip total={STEPS.length} completed={index + 1} size="md" />
              <h3 className="gb-subhead">{step.title}</h3>
              <p className="gb-body">{step.body}</p>
            </div>
          ))}
        </div>
      </Band>

      <section className={ui.stack ?? ''}>
        <h2 className="gb-subhead">Why an app?</h2>

        <div className={ui.pointGrid ?? ''}>
          <Point title="Small updates">
            Games are split into little pieces, so an update fetches what changed and nothing else.
            A one-line fix to a forty-gigabyte game is a few megabytes.
          </Point>
          <Point title="It checks itself">
            Every piece is checked before a game starts. It would rather stop than run a broken
            game.
          </Point>
          <Point title="Your computers">
            Every computer you install on shows up in a list. Lost one? Sign it out from your
            account.
          </Point>
          <Point title="Nothing extra">
            It installs, updates and starts your games. No shop inside it, no overlay, nothing
            running when you are not using it.
          </Point>
        </div>
      </section>

      <Rule />

      <section className={ui.stack ?? ''}>
        <h2 className="gb-subhead">You do not have to use it</h2>
        <p className="gb-body">
          You own the game the moment you pay. It stays in your library whatever you install, and we
          never take it back to punish anybody. If one of our games is on another shop and you would
          rather play it there, that is fine by us.
        </p>

        {/*
          The ask, repeated at the end, which is the one thing Steam's page does
          that costs nothing to copy: somebody who read the whole page is at the
          bottom of it, and the button is at the top.
        */}
        {inLauncher ? null : (
          <div className={ui.actions ?? ''}>
            <Cta href={build?.url ?? null}>Download for {platform}</Cta>
            <Cta href="/library" variant="ghost">
              Your games
            </Cta>
          </div>
        )}
      </section>
    </Page>
  );
}

/**
 * The three steps, as content rather than as markup.
 *
 * A list so the strip beside each one can count them without a number being
 * written down twice — `total` is the length and `completed` is the index, so a
 * fourth step cannot arrive with a strip still saying *of 3*.
 */
const STEPS: readonly { title: string; body: string }[] = [
  {
    title: 'Get the file',
    body: 'One installer. Run it, and the launcher puts itself on your computer.',
  },
  {
    title: 'Sign in once',
    body: 'Your games show up straight away. You only do this once on each computer.',
  },
  {
    title: 'Press play',
    body: 'It downloads the game, checks every piece, and starts it.',
  },
];

/**
 * The field at the top of the page, and the only thing above the fold.
 *
 * Three states, one shape. Somebody in the launcher is offered the screen they
 * actually want; everybody else is offered the file, whether or not there is
 * one to give them yet. The headline and the lead do not move between them,
 * because what the launcher is does not depend on who is asking.
 */
function Hero({
  build,
  platform,
  version,
  inLauncher,
  pending,
}: {
  build: LauncherBuild | null;
  /** What to call the platform, resolved by the route. See `platform` there. */
  platform: string;
  version: string | null;
  inLauncher: boolean;
  /**
   * Whether `GET /v1/site` is still out.
   *
   * Only ever used to keep quiet. "There is no build yet" and "we have not
   * asked yet" render the same unavailable button — that is deliberate, and the
   * note in the old page argues it — but the *sentence* explaining that nothing
   * is released should not be asserted before the answer arrives and then
   * removed a moment later.
   */
  pending: boolean;
}): React.ReactElement {
  const url = build?.url ?? null;
  const detail = [version ? `Version ${version}` : null, build?.note ?? null]
    .filter((part): part is string => part !== null)
    .join(' · ');

  return (
    <div data-gb-surface="ink" style={HERO}>
      <div style={HERO_INNER}>
        <Eyebrow>Download</Eyebrow>

        <h1 className="gb-display" style={{ margin: 0 }}>
          One app.
          <br />
          All your games.
        </h1>

        <p className="gb-lead">
          Get it once. It installs your games, keeps them up to date, and starts them.
        </p>

        {inLauncher ? (
          <>
            <div className={ui.actions ?? ''}>
              <Cta href="/downloads" size="lead">
                Open your downloads
              </Cta>
              <Cta href="/library" variant="ghost">
                Your games
              </Cta>
            </div>
            <p className={ui.statLabel ?? ''} style={{ margin: 0 }}>
              You are reading this inside the launcher, so you already have it.
            </p>
          </>
        ) : (
          <>
            <div className={ui.actions ?? ''}>
              <Cta href={url} size="lead">
                Download for {platform}
              </Cta>
            </div>

            {detail === '' ? null : (
              <p className={ui.statLabel ?? ''} style={{ margin: 0 }}>
                {detail}
              </p>
            )}

            {/*
              Section 8: an empty state is an invitation, not an apology — and
              section 29.2: it must not pretend a channel exists. Both are the
              same sentence here. It says what is missing, what is not missing,
              and what to do in the meantime.
            */}
            {url === null && !pending ? (
              <p className="gb-body" style={{ margin: 0 }}>
                Nothing to download yet. It turns up here the moment the first one is ready. Buying
                a game before then costs you nothing — it lands in your library when you pay, not
                when you install.
              </p>
            ) : null}

            {looksLikeWindows() === false ? (
              <p className="gb-body" style={{ margin: 0 }}>
                This is a Windows installer, and you do not look like you are on Windows. There is
                no Mac or Linux version yet.
              </p>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Whether this browser is on Windows, or plainly is not, or will not say.
 *
 * **Three values, and the third is the reason this is not `boolean`.** A user
 * agent is a hint: a string this does not recognise is not evidence of a Mac,
 * and treating it as one would tell a Windows player on some browser we have
 * never seen that the installer is not for them. So an unknown answers null and
 * the note is drawn only on a definite `false`.
 *
 * Nothing is gated on it either way — the button is there for everybody, and
 * somebody who knows better than this function can always press it. That is
 * what makes a wrong answer cost a missing sentence rather than a missing
 * download.
 *
 * Not `currentPlatform` from `/downloads`, which answers the same question for
 * a different caller and falls through to `linux`. That is correct there — it
 * runs only inside the launcher's webview, which is one of three known
 * platforms — and it is exactly the fallback this must not have.
 */
function looksLikeWindows(): boolean | null {
  const agent = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  if (agent === '') return null;
  if (/windows|win32|win64/i.test(agent)) return true;
  if (/mac os x|macintosh|linux|x11|android|iphone|ipad/i.test(agent)) return false;
  return null;
}

/*
 * The flood, edge to edge.
 *
 * `data-gb-surface="ink"` on the same element is what makes `--gb-rule` below
 * the dark-surface value rather than the page's — the attribute defines the
 * custom properties on the element that carries it, so the border resolves
 * against the field it is closing rather than against the ground behind it.
 */
const HERO: React.CSSProperties = {
  background: 'var(--gb-ink)',
  color: 'var(--gb-paper)',
  borderBottom: 'var(--gb-rule-width) solid var(--gb-rule)',
};

/*
 * The content inside it, lined up with the padded column below.
 *
 * `--gb-measure` and `--gb-gutter` are the two `.page` uses, so the headline
 * starts on the same vertical as everything under it. `border-box` for the
 * reason `.heroInner` carries it in `store.module.css`: without it the padding
 * is added outside the width and the block is two gutters wider than the field.
 *
 * **`--gb-prose` is set here rather than a `max-width` on each paragraph**, and
 * that is the pattern the token documents rather than an exception to it: the
 * measure is *the column a paragraph is in*, and this column is the whole
 * window. Left at its 100% default, the lead under a 96px headline would run
 * the full width of a wide screen — the one line on the page that has to land.
 * 52ch is the guidelines' own lead measure. The cast is because
 * `CSSProperties` has no index signature for custom properties; React sets them
 * on the element regardless.
 */
const HERO_INNER = {
  maxWidth: 'var(--gb-measure)',
  margin: '0 auto',
  boxSizing: 'border-box',
  padding: 'var(--gb-space-12) var(--gb-gutter)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 'var(--gb-space-6)',
  '--gb-prose': '52ch',
} as React.CSSProperties;
